/* eslint-disable tailwindcss/no-arbitrary-value --
   `text-[--text-secondary]` reaches the token's #424242; `text-text/secondary`
   is shadowed by the legacy palette (fe-services CLAUDE.md > Design tokens). */
import { BaseTranslations } from '../../../baseTranslations';
import type { LoanTerms } from '../../../calculatedAttributes/store';
import { formatRangeTitle } from '../../../components/RewardAsideModal/formatRangeTitle';
import { CmsOffer, GiftListItem } from '../../../types/cmsProduct';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { formatCurrencyAmount } from '../../../utils/helpers/formatters';
import {
  getLoanRewardTierSection,
  LoanRewardTier,
} from '../../../utils/helpers/personalLoanReward';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { SectionLabel } from './SectionLabel';

type RewardTierStaticText = {
  module_pl_context: string;
  module_pl_other_tiers: string;
};

interface RewardTierSectionsProps {
  label: string;
  offers: CmsOffer[];
  loanTerms: LoanTerms;
  titleOf: (item: GiftListItem) => string | undefined;
  staticText: RewardTierStaticText;
  currency: string;
}

const GIFT_TEXT = 'mhg-alpha-typography-body-2-relaxed text-text/primary';

const GiftList = ({ titles }: { titles: string[] }) => (
  <ul className={`w-full list-disc ps-6 ${GIFT_TEXT}`}>
    {titles.map((title, i) => (
      // eslint-disable-next-line react/no-array-index-key -- gift titles may repeat within a band
      <li key={`${title}-${i}`}>{title}</li>
    ))}
  </ul>
);

/** The loan basis with its two values in bold (CMS-1922 R4). */
const BasisSentence = ({
  template,
  amount,
  tenure,
}: {
  template: string;
  amount: string;
  tenure: string;
}) => (
  <p className="mhg-alpha-typography-body-2-relaxed text-text/primary">
    {template.split(/(\{amount\}|\{tenure\})/).map((part) => {
      if (part !== '{amount}' && part !== '{tenure}') return part;
      return (
        <strong
          key={part}
          className="font-font-weight/table-cell-strong text-font-size/bullet-2"
        >
          {part === '{amount}' ? amount : tenure}
        </strong>
      );
    })}
  </p>
);

/**
 * One reward source in the Personal Loan modal (CMS-1922 R4): its band label,
 * the loan basis, the tier the module's terms fall in (highlighted), then the
 * other tiers. The basis sentence sits outside the highlight: it frames the
 * whole source, not the highlighted tier alone.
 *
 * A source with no gifts renders nothing, which is how R4 omits it.
 */
export const RewardTierSections = ({
  label,
  offers,
  loanTerms,
  titleOf,
  staticText,
  currency,
}: RewardTierSectionsProps) => {
  const context = useCmsClientContext();
  const { current, other } = getLoanRewardTierSection(
    offers,
    loanTerms,
    titleOf,
  );
  const rangeText = getClientTranslations(BaseTranslations, context);
  if (!current.length && !other.length) return null;

  const rangeOf = (tier: LoanRewardTier) =>
    formatRangeTitle(currency, tier.criteria, rangeText);

  return (
    <div className="flex w-full flex-col gap-spacing/padding/x-sm">
      <SectionLabel>{label}</SectionLabel>
      <BasisSentence
        template={staticText.module_pl_context}
        amount={formatCurrencyAmount(loanTerms.amount, currency)}
        tenure={String(loanTerms.tenure)}
      />
      {current.length > 0 && (
        <div className="flex w-full flex-col gap-spacing/gap/soft-12px rounded-radius/button bg-fill/primary-light p-spacing/padding/soft-12px">
          {current.map((tier) => {
            const range = rangeOf(tier);
            return (
              <div
                key={tier.key}
                className="flex w-full flex-col gap-spacing/padding/soft-6px"
              >
                {range && (
                  <span className="mhg-alpha-typography-bullet-2 text-text/accent">
                    {range}
                  </span>
                )}
                <GiftList titles={tier.giftTitles} />
              </div>
            );
          })}
        </div>
      )}
      {other.length > 0 && (
        <>
          <p className="text-font-size/bullet-2 leading-line-height/body-2-relaxed text-[--text-secondary]">
            {staticText.module_pl_other_tiers}
          </p>
          {/* Two levels (R4): the range is the bullet, its gifts nest under it. */}
          <ul
            className={`w-full list-disc space-y-spacing/padding/soft-8px ps-6 ${GIFT_TEXT}`}
          >
            {other.map((tier) => (
              <li key={tier.key}>
                <span className="mhg-alpha-typography-bullet-2 text-text/primary">
                  {rangeOf(tier)}
                </span>
                <GiftList titles={tier.giftTitles} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
